/**
 * Admin → Address Book (staff contacts) — E05-F03 shared contract.
 *
 * 1:1 port of the legacy n8n `manage_staff` action router (docs/source-n8n/
 * reference/Staff.md → get_staff / create_contact / update_contact /
 * delete_contact). Single source of truth shared by api (route validation +
 * the `adminStaffContacts` service projection) and web (the typed staff
 * api-client + the AddressBook UI model).
 *
 * The `staff_contacts` table (schema.prisma) stores `{ id, name, email,
 * category, is_active, created_at }`. The wire DTO mirrors that row (snake_case
 * on the wire, parity with the legacy SPA). Categories are a fixed five-value
 * vocab (Sourcer / Recruiter / Manager / Notification Recipient / Other); the
 * column is free-form text so the schema accepts any string and the UI humanizes
 * the known ones with a graceful fallback.
 *
 * Transport endpoints:
 *   GET    /admin/staff       → { success:true, contacts: StaffContactDTO[] }   (get_staff)
 *   POST   /admin/staff       → { success:true, id }                            (create_contact)
 *   PATCH  /admin/staff/:id   → { success:true, id }                            (update_contact)
 *   DELETE /admin/staff/:id   → { success:true, message:'Contact deleted' }     (delete_contact, HARD delete)
 *
 * IDOR / parity notes: create requires name+email ("Name and email are
 * required"); category defaults to 'other' on the persistence side when omitted;
 * delete is a HARD delete and always answers the static legacy message.
 *
 * This file is feature-owned and re-exported from `@eer/shared` by the serial
 * Wire pass (it must NOT edit the shared barrel itself during parallel work).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Category vocab (the dialog <select> + badge map)
// ---------------------------------------------------------------------------
/**
 * The five staff categories in the legacy dialog order (Sourcer, Recruiter,
 * Manager, Notification Recipient, Other). The column is free-form text, so a
 * contact MAY carry an unknown legacy value; the UI falls back to the raw string.
 */
export const STAFF_CONTACT_CATEGORIES = [
    'sourcer',
    'recruiter',
    'manager',
    'notification',
    'other',
    'research_cc',
];
/** Human labels for the known categories (dialog options + badge text). */
export const STAFF_CONTACT_CATEGORY_LABELS = {
    sourcer: 'Sourcer',
    recruiter: 'Recruiter',
    manager: 'Manager',
    notification: 'Notification Recipient',
    other: 'Other',
    // E07-F06-T10 — the default CC of the research Publish "send to sourcers"
    // email (the sourcing teamlead + Himanshu). Appended LAST so the legacy
    // five-value dialog order is unchanged.
    research_cc: 'Research Publish CC',
};
/** Persistence default category when a create omits the field (legacy parity). */
export const STAFF_CONTACT_DEFAULT_CATEGORY = 'other';
/** The static success message the legacy `delete_contact` node always returned. */
export const STAFF_DELETE_MESSAGE = 'Contact deleted';
// ---------------------------------------------------------------------------
// Wire DTO (get_staff row shape)
// ---------------------------------------------------------------------------
/**
 * One `staff_contacts` row as projected to the wire (`Staff: Return List`).
 * `is_active` is a real boolean (the column is nullable, default true) and
 * `created_at` is an ISO-8601 string.
 */
export const staffContactSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    email: z.string(),
    /** Free-form category text; one of {@link STAFF_CONTACT_CATEGORIES} for known rows. */
    category: z.string(),
    is_active: z.boolean(),
    /** ISO 8601 creation timestamp. */
    created_at: z.string(),
});
// ---------------------------------------------------------------------------
// Request bodies (create / update)
// ---------------------------------------------------------------------------
/**
 * create_contact body. name+email required (the service re-checks with the
 * legacy "Name and email are required" message); category optional (defaults to
 * 'other' server-side when omitted).
 */
export const createStaffContactSchema = z.object({
    name: z.string().min(1, 'Name and email are required'),
    email: z.string().min(1, 'Name and email are required'),
    category: z.string().optional(),
});
/**
 * update_contact body — partial update; only the supplied fields are written
 * ("No fields to update" if none). `is_active` is NOT mutable here (parity).
 */
export const updateStaffContactSchema = z.object({
    name: z.string().optional(),
    email: z.string().optional(),
    category: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Response bodies
// ---------------------------------------------------------------------------
/** GET /admin/staff success body — the active address book. */
export const staffListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    contacts: z.array(staffContactSchema),
});
/** POST/PATCH /admin/staff success body — the affected contact id. */
export const staffMutationResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    id: z.number().int().positive(),
});
/** DELETE /admin/staff/:id success body — the static legacy message. */
export const staffDeleteResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string(),
});
//# sourceMappingURL=admin-staff.js.map